/**
 * Poll card — used on the Today screen and in the Group polls section.
 *
 * States:
 *  - open + not voted  → option buttons, bars hidden (no biasing)
 *  - open + voted       → result bars + counts, "Change vote"
 *  - closed             → muted final results, winner emphasized
 *
 * Single vs multi-choice is driven by poll.allow_multiple. Multi shows
 * checkboxes and a "Save votes" button; single votes on tap.
 */
import { useEffect, useState } from "react";

import { closePoll, getPollVoters, votePoll, type PollDTO, type PollVotersDTO } from "../api";

export function PollCard({
  poll: initial,
  onChanged,
}: {
  poll: PollDTO;
  onChanged?: (p: PollDTO) => void;
}) {
  const [poll, setPoll] = useState<PollDTO>(initial);
  const [busy, setBusy] = useState(false);
  const [showVoters, setShowVoters] = useState(false);
  // When the user has voted but wants to change, flip back to voting controls.
  const [changing, setChanging] = useState(false);
  // Local selection for multi-choice before "Save".
  const [draft, setDraft] = useState<Set<string>>(new Set(initial.my_option_ids));

  const closed = poll.status === "closed";
  // Show the voting controls if: closed=never; not voted; or actively changing.
  const showResults = closed || (poll.i_voted && !changing);
  const maxCount = Math.max(1, ...poll.options.map((o) => o.count));

  function apply(p: PollDTO) {
    setPoll(p);
    setDraft(new Set(p.my_option_ids));
    setChanging(false);
    onChanged?.(p);
  }

  async function castSingle(optionId: string) {
    if (busy || closed) return;
    setBusy(true);
    try {
      apply(await votePoll(poll.id, [optionId]));
    } finally {
      setBusy(false);
    }
  }

  async function saveMulti() {
    if (busy || closed) return;
    setBusy(true);
    try {
      apply(await votePoll(poll.id, [...draft]));
    } finally {
      setBusy(false);
    }
  }

  async function doClose() {
    if (busy) return;
    setBusy(true);
    try {
      apply(await closePoll(poll.id));
    } finally {
      setBusy(false);
    }
  }

  const winnerCount = closed ? maxCount : -1;

  return (
    <div className={"rounded-md overflow-hidden " + (closed ? "border-2 border-ink/20 bg-panel" : "border-2 border-ink")}>
      {/* Header */}
      <div className={
        "px-5 py-3 flex items-center justify-between " +
        (closed ? "bg-soft text-ink/70 border-b border-line" : "bg-ink text-bg")
      }>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-2">
          {!closed && <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />}
          Poll{closed ? " · closed" : poll.i_voted ? " · you voted" : ""}
        </span>
        <span className={"font-mono text-[11px] uppercase tracking-[0.14em] " + (closed ? "text-ink/45" : "text-bg/60")}>
          {poll.allow_multiple ? "pick any" : "pick one"}
        </span>
      </div>

      <div className="px-5 py-4">
        <div className="font-display font-bold text-xl leading-snug mb-1">{poll.question}</div>
        <div className="text-ink/55 text-sm mb-4">
          {poll.total_voters} vote{poll.total_voters === 1 ? "" : "s"}
          {closed ? " · closed" : poll.closes_at ? ` · closes ${fmtCloses(poll.closes_at)}` : " · no deadline"}
        </div>

        {/* ---- Not voted, open: option buttons ---- */}
        {!showResults && !poll.allow_multiple && (
          <div className="space-y-2">
            {poll.options.map((o) => (
              <button
                key={o.id}
                disabled={busy}
                onClick={() => castSingle(o.id)}
                className="cs-tap w-full text-left border-2 border-ink/15 hover:border-ink rounded-md px-4 py-3 font-display font-bold flex items-center justify-between group disabled:opacity-50"
              >
                {o.text}
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink/40 group-hover:text-ink">
                  tap to vote
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ---- Not voted, open, multi: checkboxes + Save ---- */}
        {!showResults && poll.allow_multiple && (
          <div className="space-y-2">
            {poll.options.map((o) => {
              const on = draft.has(o.id);
              return (
                <label
                  key={o.id}
                  className={
                    "cs-tap flex items-center gap-3 border-2 rounded-md px-4 py-3 cursor-pointer " +
                    (on ? "border-success bg-success/5" : "border-ink/15 hover:border-ink/40")
                  }
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={() => {
                      const next = new Set(draft);
                      if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                      setDraft(next);
                    }}
                  />
                  <span className={
                    "w-5 h-5 rounded grid place-items-center font-bold text-xs " +
                    (on ? "bg-success text-bg" : "border-2 border-ink/25")
                  }>{on ? "✓" : ""}</span>
                  <span className="font-display font-bold flex-1">{o.text}</span>
                </label>
              );
            })}
            <button
              disabled={busy || draft.size === 0}
              onClick={saveMulti}
              className="cs-tap mt-1 px-5 py-2.5 bg-accent text-ink font-display font-bold disabled:opacity-50"
            >
              Save votes
            </button>
          </div>
        )}

        {/* ---- Results (voted or closed) ---- */}
        {showResults && (
          <div className="space-y-3">
            {poll.options.map((o) => {
              const pct = poll.total_voters > 0 ? Math.round((o.count / maxCount) * 100) : 0;
              const isMine = poll.my_option_ids.includes(o.id);
              const isWinner = closed && o.count === winnerCount && o.count > 0;
              return (
                <div key={o.id}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-display font-bold flex items-center gap-2">
                      {o.text}
                      {isMine && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-accent text-ink px-1.5 py-0.5">
                          your vote
                        </span>
                      )}
                      {isWinner && (
                        <span className="font-mono text-[9px] uppercase tracking-wider text-success">winner</span>
                      )}
                    </span>
                    <span className="font-mono num text-ink/60">{o.count}</span>
                  </div>
                  <div className="h-2 bg-soft rounded-full overflow-hidden">
                    <div
                      className="bar h-full"
                      style={{
                        width: `${pct}%`,
                        background: isMine || isWinner ? "var(--color-success)" : "rgba(14,14,12,0.3)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ---- Footer actions ---- */}
        {(showResults || changing || poll.can_close) && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              {showResults && !poll.anonymous && (
                <button
                  onClick={() => setShowVoters((v) => !v)}
                  className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-ink underline decoration-accent decoration-2 underline-offset-2"
                >
                  {showVoters ? "Hide voters" : "See who voted →"}
                </button>
              )}
              {/* Open + voted + showing results → offer to change. */}
              {!closed && poll.i_voted && showResults && (
                <button
                  onClick={() => setChanging(true)}
                  className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-ink underline decoration-accent decoration-2 underline-offset-2"
                >
                  Change vote
                </button>
              )}
              {/* Actively changing → let them bail back to results. */}
              {changing && (
                <button
                  onClick={() => setChanging(false)}
                  className="font-mono text-[11px] uppercase tracking-wider text-ink/45 hover:text-ink"
                >
                  Cancel
                </button>
              )}
            </div>
            {poll.can_close && (
              <button
                onClick={doClose}
                disabled={busy}
                className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-warn disabled:opacity-50"
              >
                Close poll
              </button>
            )}
          </div>
        )}

        {showVoters && <Voters pollId={poll.id} />}
      </div>
    </div>
  );
}

function Voters({ pollId }: { pollId: string }) {
  const [data, setData] = useState<PollVotersDTO | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getPollVoters(pollId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [pollId]);
  if (err) return <div className="mt-3 text-sm text-warn">{err}</div>;
  if (!data) return <div className="mt-3 text-sm text-ink/45">Loading voters…</div>;
  return (
    <div className="mt-4 border-t border-line pt-4 space-y-3">
      {data.options.map((o) => (
        <div key={o.option_id}>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mb-1">
            {o.text} · {o.voters.length}
          </div>
          <div className="text-sm text-ink/75">
            {o.voters.length === 0
              ? "—"
              : o.voters.map((v) => (v.is_you ? "You" : v.name)).join(", ")}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtCloses(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "soon";
  const days = Math.round(ms / 86400000);
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.round(ms / 3600000);
  return `in ${hours} hr${hours === 1 ? "" : "s"}`;
}
